
import React, { useState ,useEffect} from 'react';
import { RiErrorWarningLine } from "react-icons/ri"; 
import { toast } from 'react-toastify';
import axios from 'axios';
import Loader from '../../loader/Loader'; 
import { useNavigate, useLocation } from 'react-router-dom';
import SerialNumberBadge from '../../components/product/SerialNumberBadge'; // Import the badge
import styles from '../products/ProductForm.module.css'; 

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialState = {
        productId: "",
        isSerialized: false,
        quantity: 1, // Default for non-serialized, derived for serialized
        selectedSerialNumbers: [], // Stores multiple selected serial numbers
        faultDescription: "",
        severity: "Low",
    };

const CreateFaultyItem = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState(initialState);
    const [availableSerials, setAvailableSerials] = useState([]);
    const [currentSerialSelection, setCurrentSerialSelection] = useState("");

    useEffect(() => {
        if (location.state && location.state.product) {
            const { product } = location.state;
            const isManagedBySerial = product.managedBySerial || false;
            
            setFormData({
                ...initialState,
                productId: product._id || "",
                isSerialized: isManagedBySerial,
                quantity: isManagedBySerial ? 0 : 1, // Initial quantity
                selectedSerialNumbers: [],
            });

            if (isManagedBySerial && product.serialNumberAvailable) {
                setAvailableSerials(product.serialNumberAvailable);
            } else {
                setAvailableSerials([]);
            }
        } else {
            setFormData(initialState);
            setCurrentSerialSelection("");
            setAvailableSerials([]);
        }
    }, [location.state]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevData => {
            let updatedValue = type === 'checkbox' ? checked : value;
            let newData = { ...prevData, [name]: updatedValue };

            if (name === 'isSerialized') {
                newData.selectedSerialNumbers = [];
                setCurrentSerialSelection("");
                if (checked) { // Switched to serialized
                    newData.quantity = 0; // Quantity derived from selected serials
                } else { // Switched to non-serialized
                    newData.quantity = 1; // Reset quantity for non-serialized input
                }
            } else if (name === 'productId') {
                // If product ID changes, reset serials (assuming it's not pre-filled)
                if (!(location.state && location.state.product && location.state.product._id === value)) {
                    setAvailableSerials([]);
                    newData.selectedSerialNumbers = [];
                    setCurrentSerialSelection("");
                    if (newData.isSerialized) {
                        newData.quantity = 0;
                    }
                }
            } else if (name === 'quantity' && !newData.isSerialized) {
                newData.quantity = parseInt(value, 10) || 0;
            }
            return newData;
        });
    };

    const handleAddSerialNumber = () => {
        if (!currentSerialSelection) {
            toast.error("Please select a serial number to add.");
            return;
        }
        if (formData.selectedSerialNumbers.includes(currentSerialSelection)) {
            toast.error("Serial number already added.");
            return;
        }
        setFormData(prevData => ({
            ...prevData,
            selectedSerialNumbers: [...prevData.selectedSerialNumbers, currentSerialSelection],
            quantity: prevData.selectedSerialNumbers.length + 1
        }));
        setCurrentSerialSelection(""); // Reset dropdown selection
    };

    const handleRemoveSerialNumber = (serialToRemove) => {
        setFormData(prevData => {
            const updatedSerials = prevData.selectedSerialNumbers.filter(sn => sn !== serialToRemove);
            return {
                ...prevData,
                selectedSerialNumbers: updatedSerials,
                quantity: updatedSerials.length
            };
        });
    };

    const validateForm = () => {
        const { productId, faultDescription, severity, isSerialized, quantity, selectedSerialNumbers } = formData;
        if (!productId.trim() || !faultDescription.trim() || !severity) {
            toast.error("Product ID, fault description, and severity are required.");
            return false;
        }
        if (isSerialized && selectedSerialNumbers.length === 0) {
            toast.error("At least one serial number must be selected for serialized items.");
            return false;
        }
        if (!isSerialized && (isNaN(parseInt(quantity, 10)) || parseInt(quantity, 10) <= 0)) {
            toast.error("A valid positive quantity is required for non-serialized items.");
            return false;
        }
        return true;
    };
    const handleMarkItemAsFaultySubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        const { productId, faultDescription, severity, isSerialized, quantity, selectedSerialNumbers } = formData;

        const faultyItemData = {
            productId,
            faultDescription,
            severity,
        };

        if (isSerialized) {
            faultyItemData.serialNumbers = selectedSerialNumbers; // Sending an array of serial numbers
            // Quantity is implicitly defined by the length of serialNumbers array on the backend
        } else {
            faultyItemData.quantity = Number(quantity);
        }

        try {
            await axios.post(`${BACKEND_URL}/api/faulty-items`, faultyItemData);
            toast.success("Item marked as faulty successfully!");
            setFormData(initialState); // Reset form
            setCurrentSerialSelection("");
            navigate('/FaultyItemsPage'); // Navigate to the list of faulty items or another appropriate page
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to mark item as faulty.');
            console.error("Failed to mark item as faulty:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.formContainer}>
            {isLoading && <Loader />}
            <div className={styles.formCard}>
                <div className={styles.topLine}></div>
                <button 
                    className={styles.backButton}
                    onClick={() => navigate('/FaultyItemsPage')} // Adjust navigation as needed
                >
                    ← Back
                </button>
                <div className={styles.cardHeader}>
                    <RiErrorWarningLine size={28} />
                    <h2 className={styles.title}>Mark New Item as Faulty</h2>
                </div>

                <form onSubmit={handleMarkItemAsFaultySubmit}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Severity
                            </label>
                            <select
                                name="severity"
                                value={formData.severity}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                required
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                                <option value="Critical">Critical</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        {formData.isSerialized ? (
                            <>
                                <div className={styles.formGroup}>
                                    <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                        Select Serial Number(s)
                                    </label>
                                    <div className={styles.serialInputRow}>
                                        <select
                                            name="currentSerialSelection"
                                            value={currentSerialSelection}
                                            onChange={(e) => setCurrentSerialSelection(e.target.value)}
                                            className={styles.formInput}
                                            disabled={availableSerials.filter(s => !formData.selectedSerialNumbers.includes(s)).length === 0}
                                        >
                                            <option value="">-- Select a Serial Number --</option>
                                            {availableSerials
                                                .filter(serial => !formData.selectedSerialNumbers.includes(serial))
                                                .map(serial => (
                                                    <option key={serial} value={serial}>
                                                        {serial}
                                                    </option>
                                                ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={handleAddSerialNumber}
                                            className={styles.addSerialBtn}
                                            disabled={!currentSerialSelection}
                                        >
                                            Add
                                        </button>
                                    </div>
                                    {availableSerials.length === 0 && formData.productId && (
                                        <small className={styles.fieldHint}>
                                            No available serial numbers for this product.
                                        </small>
                                    )}
                                    <div className={`${styles.serialTags} ${styles.largerSerialBadge}`} style={{ marginTop: '10px' }}>
                                        {formData.selectedSerialNumbers.map((serial, idx) => (
                                            // Use SerialNumberBadge component here
                                            <SerialNumberBadge
                                                key={serial} // Use the serial number itself as the key
                                                serialNumber={serial}
                                                onRemove={() => handleRemoveSerialNumber(serial)}
                                                isRemovable={true}
                                            />
                                        ))}
                                    </div>
                                </div>

                            </>
                        ) : (
                            <div className={styles.formGroup}>
                                <label className={`${styles.formLabel} ${styles.requiredLabel}`}>Quantity</label>
                                <input type="number" className={styles.formInput} placeholder="Enter Quantity" name="quantity" value={formData.quantity} onChange={handleInputChange} min="1" required={!formData.isSerialized} />
                            </div>
                        )}
                    </div>

                    <div className={styles.formRow}>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Fault Description
                            </label>
                            <textarea
                                className={styles.textareaInput}
                                placeholder="Describe the fault or reason for marking as faulty"
                                name="faultDescription"
                                value={formData.faultDescription}
                                onChange={handleInputChange}
                                rows="4"
                                required
                            />
                        </div>
                    </div>

                    <button type="submit" className={styles.submitBtn}>
                        Mark as Faulty
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreateFaultyItem;

// You might need to add/ensure these styles in your ProductForm.module.css or a shared CSS file:
// .serialTagItem { display: inline-flex; align-items: center; background-color: #e0e0e0; padding: 4px 8px; border-radius: 16px; margin-right: 8px; margin-bottom: 8px; font-size: 0.9em; }
// .removeSerialBtn { background: none; border: none; color: #777; margin-left: 8px; cursor: pointer; font-size: 1.2em; padding: 0; line-height: 1; }
// .removeSerialBtn:hover { color: #333; }
// Adjust styles.serialInputRow and styles.addSerialBtn if necessary for the select + button layout.
